import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, SecondaryButton } from "../../components/crm/Form";
import NotificationStatusBadge from "../../components/notifications/NotificationStatusBadge";
import api, { formatApiError } from "../../lib/api";

const EVENT_LABEL = {
  billing_past_due: "Billing past due",
  billing_unpaid: "Billing unpaid",
  ack_received: "Owner acknowledged",
  ack_requested: "Reminder sent",
};

export default function NotificationDetail() {
  const { id } = useParams();
  const [n, setN] = useState(null);
  const [err, setErr] = useState("");
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get(`/notifications/${id}`);
      setN(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/notifications/${id}/retry`);
      toast.success("Retry attempted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(false);
    }
  };

  if (!n) {
    return (
      <Layout>
        <ErrorBanner message={err} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title={EVENT_LABEL[n.event_type] || n.event_type}
          subtitle={n.subject}
          actions={
            n.status === "failed" || n.status === "stubbed_no_provider" ? (
              <SecondaryButton
                onClick={retry}
                disabled={retrying}
                data-testid="notif-detail-retry"
              >
                {retrying ? "Retrying…" : "Retry delivery"}
              </SecondaryButton>
            ) : null
          }
        />
        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <NotificationStatusBadge status={n.status} />
            <span className="text-[11px] text-slate-500">
              {n.provider ? `via ${n.provider}` : ""}
              {n.provider_message_id ? ` · ${n.provider_message_id}` : ""}
            </span>
          </div>

          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Item label="Created" value={new Date(n.created_at).toLocaleString()} />
            <Item
              label="Last attempt"
              value={
                n.last_attempt_at
                  ? new Date(n.last_attempt_at).toLocaleString()
                  : "—"
              }
            />
            <Item
              label="Sent"
              value={n.sent_at ? new Date(n.sent_at).toLocaleString() : "—"}
            />
            <Item label="Attempts" value={String(n.attempt_count)} />
            <Item
              label="Recipient kind"
              value={n.recipient_kind === "internal_users" ? "Internal" : "Customer"}
            />
            <Item
              label="Recipients"
              value={(n.recipient_emails || []).join(", ") || "—"}
            />
            <Item label="Channel" value={n.channel} />
            <Item label="Dedupe key" value={n.dedupe_key || "—"} mono />
          </dl>

          {n.error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <strong>Error:</strong> {n.error}
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 pt-2 border-t border-slate-200">
            {n.related_subscription_id && (
              <Link
                to={`/subscriptions/${n.related_subscription_id}`}
                className="font-semibold text-slate-700 hover:text-slate-900"
              >
                Open subscription →
              </Link>
            )}
            {n.related_report_id && n.related_work_order_id && (
              <Link
                to={`/work-orders/${n.related_work_order_id}/report`}
                className="font-semibold text-slate-700 hover:text-slate-900"
              >
                Open report →
              </Link>
            )}
            {n.related_customer_id && (
              <Link
                to={`/customers/${n.related_customer_id}`}
                className="font-semibold text-slate-700 hover:text-slate-900"
              >
                Open customer →
              </Link>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">
              Rendered email preview
            </div>
            <div className="text-[11px] text-slate-500">HTML</div>
          </div>
          <iframe
            data-testid="notif-html-preview"
            title="email-preview"
            srcDoc={n.body_html}
            className="w-full bg-white"
            style={{ height: 520, border: 0 }}
            sandbox=""
          />
        </div>

        <details className="bg-white border border-slate-200 rounded-md p-5">
          <summary className="cursor-pointer text-sm font-semibold text-slate-900">
            Plain-text version
          </summary>
          <pre
            data-testid="notif-text-preview"
            className="mt-3 whitespace-pre-wrap font-data text-xs text-slate-700 bg-slate-50 rounded-md p-3"
          >
            {n.body_text}
          </pre>
        </details>
      </div>
    </Layout>
  );
}

function Item({ label, value, mono }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </dt>
      <dd
        className={`text-slate-900 ${mono ? "font-data text-xs" : ""} break-all`}
      >
        {value}
      </dd>
    </>
  );
}
